// Mobile drill-down for a 3-level target item (e.g. SHOP), used in the header
// drawer. Trigger opens a full-screen panel (screen 1 = categories); tapping a
// category slides in its sub-screen (screen 2); tapping a sub-category with its
// own children slides in the third level (screen 3). Back steps up one level
// (each screen knows its parent); close hides everything.

class ShopMegaMobile extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-shop-mega-open]');
    this.panel = this.querySelector('[data-shop-mega-panel]');
    if (!this.trigger || !this.panel) return;

    // Move the panel to <body> so position:fixed covers the viewport (drawer
    // ancestors may use transforms that would trap it).
    if (this.panel.parentElement !== document.body) {
      document.body.appendChild(this.panel);
    }

    this.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      this.open();
    });

    this.panel.querySelectorAll('[data-shop-mega-cat]').forEach((btn) => {
      btn.addEventListener('click', () => this.showScreen(btn.getAttribute('data-shop-mega-cat')));
    });

    // Back returns to the parent screen encoded on the button (one level up),
    // not always to the root — so the third level returns to the second.
    this.panel.querySelectorAll('[data-shop-mega-back]').forEach((btn) => {
      btn.addEventListener('click', () => this.showScreen(btn.getAttribute('data-shop-mega-back') || 'root'));
    });

    // Root back: close our panel and return to the drawer.
    this.panel.querySelectorAll('[data-shop-mega-exit]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    // Close (X): close our panel and the whole drawer.
    this.panel.querySelectorAll('[data-shop-mega-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeAll());
    });

    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.close();
    };
  }

  open() {
    this.panel.hidden = false;
    this.showScreen('root');
    requestAnimationFrame(() => this.panel.classList.add('is-open'));
    document.documentElement.classList.add('shop-mega-open');
    document.addEventListener('keydown', this.onKeydown);
  }

  close() {
    this.panel.classList.remove('is-open');
    document.documentElement.classList.remove('shop-mega-open');
    document.removeEventListener('keydown', this.onKeydown);
    const hide = () => {
      this.panel.removeEventListener('transitionend', hide);
      if (!this.panel.classList.contains('is-open')) {
        this.panel.hidden = true;
        this.showScreen('root');
      }
    };
    this.panel.addEventListener('transitionend', hide);
    setTimeout(hide, 400);
  }

  // Close our panel and the whole header drawer (the X behaves like Horizon's).
  closeAll() {
    this.close();
    const drawer = document.getElementById('Details-menu-drawer-container');
    if (drawer && drawer.open) drawer.open = false;
  }

  // The chain of screens from `name` up to (but excluding) root, deepest first.
  // Each screen declares its parent via data-shop-mega-parent, so we can keep
  // the ancestors active underneath while the current screen slides over them.
  activeChain(name) {
    const chain = [];
    let cur = name;
    let guard = 0;
    while (cur && cur !== 'root' && guard < 20) {
      chain.push(cur);
      const el = this.panel.querySelector(`[data-shop-mega-screen="${CSS.escape(cur)}"]`);
      cur = el ? el.getAttribute('data-shop-mega-parent') : null;
      guard += 1;
    }
    return chain; // e.g. ['subcat-2-1', 'cat-2']
  }

  showScreen(name) {
    const chain = name === 'root' ? [] : this.activeChain(name);
    this.panel.querySelectorAll('[data-shop-mega-screen]').forEach((screen) => {
      const sname = screen.getAttribute('data-shop-mega-screen');
      // The root screen is always present underneath; sub-screens slide over it.
      if (sname === 'root') {
        screen.hidden = false;
        return;
      }
      const depth = chain.indexOf(sname); // -1 = not in the active chain
      const isActive = depth !== -1;
      screen.classList.toggle('is-active', isActive);
      if (isActive) {
        // Deepest screen (index 0) sits highest; ancestors stack below it.
        screen.style.zIndex = String(chain.length - depth);
        screen.hidden = false;
      } else {
        // Stay above the parent while sliding out, so the exit animation shows
        // (otherwise the opaque parent screen would cover it instantly).
        screen.style.zIndex = '999';
        // Keep it in the DOM but slid away; hide after the transition.
        const el = screen;
        setTimeout(() => {
          if (!el.classList.contains('is-active')) {
            el.hidden = true;
            el.style.zIndex = '';
          }
        }, 350);
      }
    });
  }
}

if (!customElements.get('shop-mega-mobile')) {
  customElements.define('shop-mega-mobile', ShopMegaMobile);
}
